import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, Spinner,
    Modal, ModalHeader, ModalBody, UncontrolledCarousel,
    Form, FormGroup
} from 'reactstrap';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import SweetAlert from 'react-bootstrap-sweetalert';

const Posts = () => {
    document.title = "Posts | Koottu Admin";
    
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPosts, setSelectedPosts] = useState([]);
    const [viewModal, setViewModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [showSweetAlert, setShowSweetAlert] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [suspendReason, setSuspendReason] = useState('');
    const [filters, setFilters] = useState({
        limit: 10,
        search: '',
        sortBy: '',
        fromDate: '',
        toDate: '',
        page: 1
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalCount: 0
    });

    const fetchPosts = async () => {
        try {
            setLoading(true);
            let url = `/user/posts/?page=${filters.page}&status=1&size=${filters.limit}`;
            
            // Add search filter
            if (filters.search) {
                url += `&search=${filters.search}`;
            }
            
            // Add sort filter
            if (filters.sortBy) {
                url += `&sortBy=${filters.sortBy}`;
            }
            
            // Add date filters
            if (filters.fromDate) {
                url += `&fromDate=${moment(filters.fromDate).format('YYYY-MM-DD')}`;
            }
            if (filters.toDate) {
                url += `&toDate=${moment(filters.toDate).format('YYYY-MM-DD')}`;
            }

            const response = await APIClient.get(url);
            if (response.data && response.data.result) {
                setPosts(response.data.result);
                setPagination(prev => ({
                    ...prev,
                    totalCount: response.data.totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
            toast.error("Failed to fetch posts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [filters]);

    const handlePostAction = async (action) => {
        try {
            setLoading(true);
            let payload = {
                postId: selectedPosts,
                status: action === 'inactive' ? 0 : action === 'suspend' ? 2 : 4,
                reason: action === 'suspend' ? suspendReason : 'ok'
            };

            await APIClient.post('/assets/', payload);
            toast.success(`Posts ${action}d successfully`);
            fetchPosts();
            setSelectedPosts([]);
            setShowSweetAlert(false);
            setSuspendReason('');
        } catch (error) {
            console.error(`Error ${action}ing posts:`, error);
            toast.error(`Failed to ${action} posts`);
        } finally {
            setLoading(false);
        }
    };

    const openActionConfirmation = (action) => {
        setActionType(action);
        setShowSweetAlert(true);
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / filters.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= filters.page - delta && i <= filters.page + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">Showing <span className="fw-semibold">{posts.length}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results</div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${filters.page === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={filters.page === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === filters.page ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && setFilters(prev => ({ ...prev, page: pageNumber }))}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${filters.page === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={filters.page === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    const resetFilters = () => {
        setFilters({
            limit: 10,
            search: '',
            sortBy: '',
            fromDate: '',
            toDate: '',
            page: 1
        });
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Posts" pageTitle="Posts" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center">
                                <h4 className="card-title mb-0 flex-grow-1">Posts</h4>
                                <div className="d-flex gap-2">
                                    <Button 
                                        color="warning" 
                                        disabled={selectedPosts.length === 0}
                                        onClick={() => openActionConfirmation('inactive')}
                                    >
                                        Make Inactive
                                    </Button>
                                    <Button 
                                        color="danger" 
                                        disabled={selectedPosts.length === 0}
                                        onClick={() => openActionConfirmation('suspend')}
                                    >
                                        Suspend
                                    </Button>
                                    <Button 
                                        color="dark" 
                                        disabled={selectedPosts.length === 0}
                                        onClick={() => openActionConfirmation('delete')}
                                    >
                                        Delete
                                    </Button>
                                    <UncontrolledDropdown>
                                        <DropdownToggle tag="button" className="btn btn-soft-info">
                                            <i className="ri-filter-3-line align-bottom"></i> Filter
                                        </DropdownToggle>
                                        <DropdownMenu className="p-3" style={{ minWidth: "300px" }}>
                                            <div className="mb-3">
                                                <Label>Creator Name</Label>
                                                <Input 
                                                    type="text"
                                                    value={filters.search}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                                    placeholder="Search by creator name..."
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Label>Sort By</Label>
                                                <Input 
                                                    type="select"
                                                    value={filters.sortBy}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                                                >
                                                    <option value="">Select</option>
                                                    <option value="1">Most Liked</option>
                                                    <option value="2">Most Commented</option>
                                                </Input>
                                            </div>
                                            <div className="mb-3">
                                                <Label>Date Range</Label>
                                                <Flatpickr
                                                    className="form-control"
                                                    options={{
                                                        mode: "range",
                                                        dateFormat: "Y-m-d",
                                                        static: true,
                                                        clickOpens: true,
                                                        closeOnSelect: false,
                                                        allowInput: true
                                                    }}
                                                    value={[filters.fromDate, filters.toDate].filter(Boolean)}
                                                    onChange={dates => setFilters(prev => ({
                                                        ...prev,
                                                        fromDate: dates[0] || '',
                                                        toDate: dates[1] || ''
                                                    }))}
                                                />
                                            </div>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                    <Button 
                                        color="warning" 
                                        onClick={resetFilters}
                                    >
                                        <i className="ri-refresh-line align-bottom"></i> Reset Filters
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>
                                                    <Input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedPosts(posts.map(post => post.postId));
                                                            } else {
                                                                setSelectedPosts([]);
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th>SL No</th>
                                                <th>Creator Name/Username</th>
                                                <th>Media Type</th>
                                                <th>Date & Time</th>
                                                <th>Price</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {posts.map((post, index) => (
                                                <tr key={post.postId}>
                                                    <td>
                                                        <Input
                                                            type="checkbox"
                                                            checked={selectedPosts.includes(post.postId)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedPosts([...selectedPosts, post.postId]);
                                                                } else {
                                                                    setSelectedPosts(selectedPosts.filter(id => id !== post.postId));
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{`${post.firstName} ${post.lastName}`}<br/>{post.username}</td>
                                                    <td>{post.mediaType === 1 ? 'Video' : 'Image'}</td>
                                                    <td>{moment(post.creationTs).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>{post.price || 0}</td>
                                                    <td>{post.email}</td>
                                                    <td>{`${post.countryCode} ${post.phoneNumber}`}</td>
                                                    <td>
                                                        <Button 
                                                            color="primary" 
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => {
                                                                setSelectedPost(post);
                                                                setViewModal(true);
                                                            }}
                                                        >
                                                            View Post
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {posts.length === 0 && (
                                                <tr>
                                                    <td colSpan="9" className="text-center">
                                                        No posts found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {loading && (
                <div className="overlay">
                    <Spinner color="primary" />
                </div>
            )}
            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
                size="lg"
            >
                <ModalHeader toggle={() => setViewModal(!viewModal)}>
                    Post Details
                </ModalHeader>
                <ModalBody>
                    {selectedPost && (
                        <div>
                            {/* Post Content */}
                            {selectedPost.description && (
                                <div className="mb-4">
                                    <h5 className="text-muted mb-3">Description</h5>
                                    <p>{selectedPost.description}</p>
                                </div>
                            )}

                            {/* Post Media */}
                            {selectedPost.postData && selectedPost.postData.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="text-muted mb-3">Content</h5>
                                    {selectedPost.mediaType === 1 ? (
                                        <div className="ratio ratio-16x9">
                                            <iframe 
                                                src={selectedPost.postData[0].url}
                                                title="Post Content"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    ) : (
                                        <img
                                            src={selectedPost.postData[0].url}
                                            alt="Post Content"
                                            className="img-fluid"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Post Info */}
                            <div className="mb-4">
                                <h5 className="text-muted mb-3">Post Information</h5>
                                <div className="table-responsive">
                                    <Table className="table-borderless mb-0">
                                        <tbody>
                                            <tr>
                                                <th scope="row">Post Type</th>
                                                <td>{selectedPost.postTypeText}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row">Price</th>
                                                <td>{selectedPost.price || 0}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row">Status</th>
                                                <td>{selectedPost.statusText}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row">Created On</th>
                                                <td>{moment(selectedPost.creationTs).format('DD MMM YYYY, hh:mm A')}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>
            <SweetAlert
                show={showSweetAlert}
                title="Confirm Action"
                onConfirm={() => handlePostAction(actionType)}
                onCancel={() => setShowSweetAlert(false)}
                confirmBtnText="Confirm"
                cancelBtnText="Cancel"
            >
                {actionType === 'suspend' ? (
                    <Form>
                        <FormGroup>
                            <Label>Suspend Reason</Label>
                            <Input
                                type="text"
                                value={suspendReason}
                                onChange={(e) => setSuspendReason(e.target.value)}
                            />
                        </FormGroup>
                    </Form>
                ) : null}
            </SweetAlert>
        </div>
    );
};

export default Posts;